import React from 'react'
import {
  Controller,
  FieldPath,
  FieldPathValue,
  FieldValues,
  useFormContext
} from 'react-hook-form'

import { RawInputFieldProps, RawInputField } from './RawInputField'
import { z } from 'zod'
import { RawInputFieldMeasuerement } from './RawInputFieldMeasuerement'

type RHFInputFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Omit<RawInputFieldProps, 'value' | 'onChange' | 'id' | 'errorText'> & {
  name: TName
  id?: string
  label?: string
  variant?: 'primary' | 'measurement'
  helperMessage?: string
  defaultValue?: FieldPathValue<TFieldValues, TName>
}

export function InputField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  id,
  label,
  defaultValue,
  variant = 'primary',
  helperMessage,
  ...props
}: RHFInputFieldProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>()
  const inputId = id ?? String(name)
  const visualLabel = label ?? String(name)
  const controllerDefault = (defaultValue ?? ('' as unknown)) as FieldPathValue<
    TFieldValues,
    TName
  >
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={controllerDefault}
      render={({ field, fieldState }) => (
        <>
          {variant === 'primary' ? (
            <RawInputField
              id={inputId}
              label={visualLabel}
              fieldName={String(name)}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              errorText={fieldState.error?.message ?? null}
              helperMessage={helperMessage}
              {...props}
            />
          ) : (
            <RawInputFieldMeasuerement
              id={inputId}
              label={visualLabel}
              fieldName={String(name)}
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              errorText={fieldState.error?.message ?? null}
              helperMessage={helperMessage}
              {...props}
            />
          )}
        </>
      )}
    />
  )
}

const defaultSchemas = {
  text: (required: boolean) =>
    required
      ? z.string().min(1, 'This field is required')
      : z.string().optional(),
  email: (required: boolean) =>
    required
      ? z
          .string()
          .min(1, 'This field is required')
          .email('Invalid email address')
      : z.string().email('Invalid email address').optional()
}

InputField.getSchema = (
  type: 'text' | 'email' = 'text',
  required = false,
  override?: z.ZodTypeAny
) => override ?? defaultSchemas[type](required)
